/**
 * Inline Lucide icons (MIT). Each entry is the *inner* SVG content; we
 * wrap it in a properly-attributed <svg> via the `icon()` factory.
 *
 * Source: https://lucide.dev — using a small hand-picked subset to avoid
 * pulling the npm package for 6 icons.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

type Attrs = Record<string, string | number>;
interface IconShape {
  tag: string;
  attrs: Attrs;
}

const ICONS: Record<string, IconShape[]> = {
  play: [
    { tag: 'polygon', attrs: { points: '6 3 20 12 6 21 6 3', fill: 'currentColor', stroke: 'none' } },
  ],
  stop: [
    { tag: 'rect', attrs: { x: 6, y: 6, width: 12, height: 12, rx: 1.5, fill: 'currentColor', stroke: 'none' } },
  ],
  circle: [
    { tag: 'circle', attrs: { cx: 12, cy: 12, r: 5, fill: 'currentColor', stroke: 'none' } },
  ],
  'volume-2': [
    { tag: 'polygon', attrs: { points: '11 5 6 9 2 9 2 15 6 15 11 19 11 5' } },
    { tag: 'path', attrs: { d: 'M15.54 8.46a5 5 0 0 1 0 7.07' } },
    { tag: 'path', attrs: { d: 'M19.07 4.93a10 10 0 0 1 0 14.14' } },
  ],
  'volume-x': [
    { tag: 'polygon', attrs: { points: '11 5 6 9 2 9 2 15 6 15 11 19 11 5' } },
    { tag: 'line', attrs: { x1: 22, x2: 16, y1: 9, y2: 15 } },
    { tag: 'line', attrs: { x1: 16, x2: 22, y1: 9, y2: 15 } },
  ],
  'rotate-ccw': [
    { tag: 'path', attrs: { d: 'M3 12a9 9 0 1 0 3-6.7L3 8' } },
    { tag: 'path', attrs: { d: 'M3 3v5h5' } },
  ],
  'trash-2': [
    { tag: 'path', attrs: { d: 'M3 6h18' } },
    { tag: 'path', attrs: { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' } },
    { tag: 'path', attrs: { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' } },
    { tag: 'line', attrs: { x1: 10, x2: 10, y1: 11, y2: 17 } },
    { tag: 'line', attrs: { x1: 14, x2: 14, y1: 11, y2: 17 } },
  ],
  plus: [
    { tag: 'path', attrs: { d: 'M5 12h14' } },
    { tag: 'path', attrs: { d: 'M12 5v14' } },
  ],
  minus: [
    { tag: 'path', attrs: { d: 'M5 12h14' } },
  ],
  'sliders-vertical': [
    { tag: 'line', attrs: { x1: 4, x2: 4, y1: 21, y2: 14 } },
    { tag: 'line', attrs: { x1: 4, x2: 4, y1: 10, y2: 3 } },
    { tag: 'line', attrs: { x1: 12, x2: 12, y1: 21, y2: 12 } },
    { tag: 'line', attrs: { x1: 12, x2: 12, y1: 8, y2: 3 } },
    { tag: 'line', attrs: { x1: 20, x2: 20, y1: 21, y2: 16 } },
    { tag: 'line', attrs: { x1: 20, x2: 20, y1: 12, y2: 3 } },
    { tag: 'line', attrs: { x1: 2, x2: 6, y1: 14, y2: 14 } },
    { tag: 'line', attrs: { x1: 10, x2: 14, y1: 8, y2: 8 } },
    { tag: 'line', attrs: { x1: 18, x2: 22, y1: 16, y2: 16 } },
  ],
  'sliders-horizontal': [
    { tag: 'line', attrs: { x1: 21, x2: 14, y1: 4, y2: 4 } },
    { tag: 'line', attrs: { x1: 10, x2: 3, y1: 4, y2: 4 } },
    { tag: 'line', attrs: { x1: 21, x2: 12, y1: 12, y2: 12 } },
    { tag: 'line', attrs: { x1: 8, x2: 3, y1: 12, y2: 12 } },
    { tag: 'line', attrs: { x1: 21, x2: 16, y1: 20, y2: 20 } },
    { tag: 'line', attrs: { x1: 12, x2: 3, y1: 20, y2: 20 } },
    { tag: 'line', attrs: { x1: 14, x2: 14, y1: 2, y2: 6 } },
    { tag: 'line', attrs: { x1: 8, x2: 8, y1: 10, y2: 14 } },
    { tag: 'line', attrs: { x1: 16, x2: 16, y1: 18, y2: 22 } },
  ],
  x: [
    { tag: 'path', attrs: { d: 'M18 6 6 18' } },
    { tag: 'path', attrs: { d: 'm6 6 12 12' } },
  ],
  pause: [
    { tag: 'rect', attrs: { x: 6, y: 5, width: 4, height: 14, rx: 0.5, fill: 'currentColor', stroke: 'none' } },
    { tag: 'rect', attrs: { x: 14, y: 5, width: 4, height: 14, rx: 0.5, fill: 'currentColor', stroke: 'none' } },
  ],
  'skip-back': [
    { tag: 'polygon', attrs: { points: '19 20 9 12 19 4 19 20', fill: 'currentColor' } },
    { tag: 'line', attrs: { x1: 5, x2: 5, y1: 19, y2: 5 } },
  ],
};

export type IconName = keyof typeof ICONS;

/**
 * Create an SVG element rendering a Lucide icon. Size defaults to 16px;
 * pass a number to override (icon scales via viewBox).
 *
 * Color is inherited from the parent's `color` via `stroke: currentColor`,
 * so wrapping with a coloured button or text node tints the icon.
 */
export function icon(name: IconName, size = 16): SVGSVGElement {
  const shapes = ICONS[name];
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '2');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.classList.add('icon');
  svg.setAttribute('aria-hidden', 'true');

  for (const s of shapes) {
    const el = document.createElementNS(SVG_NS, s.tag);
    for (const [k, v] of Object.entries(s.attrs)) {
      el.setAttribute(k, String(v));
    }
    svg.appendChild(el);
  }
  return svg;
}
